import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import { Button, InputAdornment, Pagination, Skeleton, TextField } from "@mui/material";
import { Image as ImageIcon, Search, ShoppingBag, ShoppingCart, X } from "react-feather";
import { useWebsite } from "../../../../context/WebsiteContext";
import { fetchProducts, formatPrice } from "../shopApi";
import FilterSort from "./FilterSort";
import styles from "../shop.module.css";

const FILTER_KEYS = ["search", "availability", "price_min", "price_max", "on_sale", "sort", "page"];
const PER_PAGE = 12;

// Whole-number "% off" for a sale item
export const savePercent = (item) => {
  const was = Number(item.compare_at_price);
  return item.on_sale && was > 0 ? Math.round(((was - Number(item.price)) / was) * 100) : 0;
};

// Price with the struck "was" price when the item is on sale
export const PriceTag = ({ item, className }) => (<span className={className || styles.Price}>
  <span className={item.on_sale ? styles.SalePrice : undefined}>{formatPrice(item.price)}</span>
  {item.on_sale && <s className={styles.WasPrice}>{formatPrice(item.compare_at_price)}</s>}
</span>);

// Sold out wins over Sale
export const ItemBadge = ({ item }) => {
  if (!item.in_stock) return <span className={styles.Badge}>Sold out</span>;
  if (item.on_sale) return <span className={`${styles.Badge} ${styles.BadgeSale}`}>{savePercent(item) > 0 ? `-${savePercent(item)}%` : "Sale"}</span>;
  if (item.low_stock) return <span className={`${styles.Badge} ${styles.BadgeLow}`}>Only {item.max_qty} left</span>;
  return null;
};

// Card-shaped placeholders while the items load
const GridSkeleton = () => (<div className={styles.Grid}>
  {Array.from({ length: 8 }).map((_, i) => (<div className={styles.Card} key={i}>
    <Skeleton variant="rectangular" className={styles.CardImageLink} sx={{ height: "auto" }} />
    <div className={styles.CardBody}>
      <Skeleton width="80%" />
      <Skeleton width="40%" />
      <Skeleton variant="rounded" height={34} sx={{ mt: 1 }} />
    </div>
  </div>))}
</div>);

/**
 * Store collection page: search, "Filter and sort", product count and an item grid.
 * Filters live in the URL (?availability=in&sort=price_asc), so links and the back button keep them.
 * basePath: "/shop" (public) or "/customer/shop".
 */
const ProductCatalogue = ({ basePath, onAdd, addingId }) => {
  const { currentDomain } = useWebsite();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => {
    const f = {};
    FILTER_KEYS.forEach((k) => { f[k] = searchParams.get(k) || ""; });
    return f;
  }, [searchParams]);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [facets, setFacets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(filters.search);

  // Any filter change goes back to page 1
  const update = (partial) => {
    const next = new URLSearchParams(searchParams);
    Object.entries({ page: "", ...partial }).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined) next.delete(k); else next.set(k, v);
    });
    setSearchParams(next);
  };

  const debouncedSearch = useMemo(() => debounce((value) => update({ search: value }), 400), [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    setLoading(true);
    const params = { per_page: PER_PAGE };
    FILTER_KEYS.forEach((k) => { if (filters[k]) params[k] = filters[k]; });
    fetchProducts(currentDomain, params).then((response) => {
      if (response.data.status === "SUCCESS") {
        setItems(response.data.data || []);
        setMeta(response.data.meta || { current_page: 1, last_page: 1, total: 0 });
        setFacets(response.data.facets || null);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [currentDomain, filters]);

  const filtered = filters.search || filters.availability || filters.price_min || filters.price_max || filters.on_sale;
  const clearSearch = () => { setSearchInput(""); debouncedSearch.cancel(); update({ search: "" }); };
  const clearAll = () => { setSearchInput(""); debouncedSearch.cancel(); update({ search: "", availability: "", price_min: "", price_max: "", on_sale: "" }); };

  const search = (<TextField
    fullWidth
    size="small"
    placeholder="Search items"
    value={searchInput}
    onChange={(e) => { setSearchInput(e.target.value); debouncedSearch(e.target.value); }}
    InputProps={{
      className: styles.SearchInput,
      startAdornment: <InputAdornment position="start"><Search size={16} /></InputAdornment>,
      endAdornment: searchInput ? <InputAdornment position="end"><button type="button" className={styles.ClearBU} onClick={clearSearch} aria-label="Clear search"><X size={14} /></button></InputAdornment> : null,
    }}
  />);

  return (<>
    <FilterSort filters={filters} facets={facets} total={meta.total || 0} onChange={update} search={search} />

    {loading && <GridSkeleton />}

    {!loading && items.length === 0 && (<div className={styles.EmptyState}>
      <span className={styles.EmptyIcon}><ShoppingBag size={26} /></span>
      <strong>{filtered ? "No items match your search" : "The shop is empty for now"}</strong>
      <span>{filtered ? "Try a different word or remove some filters." : "Please check back soon."}</span>
      {filtered && <Button className={styles.SecondaryBU} onClick={clearAll}>Clear search and filters</Button>}
    </div>)}

    {!loading && items.length > 0 && (<div className={styles.Grid}>
      {items.map((item) => (<div className={`${styles.Card} ${!item.in_stock ? styles.CardSoldOut : ""}`} key={item.id}>
        <Link to={`${basePath}/${item.id}`} className={styles.CardImageLink} aria-label={item.title}>
          {item.images?.length > 0 ? <>
            <img src={item.images[0]} alt="" className={styles.CardImage} loading="lazy" />
            {item.images[1] && <img src={item.images[1]} alt="" className={`${styles.CardImage} ${styles.CardImageHover}`} loading="lazy" />}
          </> : <span className={styles.NoImage}><ImageIcon size={32} /></span>}
          <ItemBadge item={item} />
        </Link>
        <div className={styles.CardBody}>
          <Link to={`${basePath}/${item.id}`} className={styles.CardTitle} title={item.title}>{item.title}</Link>
          <PriceTag item={item} />
          <div className={styles.CardFoot}>
            {item.in_stock
              ? <Button fullWidth size="small" className={styles.PrimaryBU} startIcon={<ShoppingCart size={15} />} disabled={addingId === item.id} onClick={() => onAdd(item, 1)}>{addingId === item.id ? "Adding…" : "Add to cart"}</Button>
              : <Button fullWidth size="small" className={styles.SecondaryBU} disabled>Sold out</Button>}
          </div>
        </div>
      </div>))}
    </div>)}

    {!loading && meta.total > 0 && (<div className={styles.Pager}>
      <span className={styles.PagerInfo}>Showing {(meta.current_page - 1) * PER_PAGE + 1}–{Math.min(meta.current_page * PER_PAGE, meta.total)} of {meta.total}</span>
      {meta.last_page > 1 && <Pagination shape="rounded" count={meta.last_page} page={meta.current_page} onChange={(e, page) => { update({ ...filters, page: page > 1 ? String(page) : "" }); window.scrollTo({ top: 0, behavior: "smooth" }); }} />}
    </div>)}
  </>);
};

export default ProductCatalogue;
